import {countries as countriesData} from 'countries-list'
import type {Key} from 'react-aria-components'
import {
  ComboBox,
  FieldError,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
} from 'react-aria-components'

import type {PaxCountry} from '@/lib/flight/flightModel'
import {isCountryCode} from '@/lib/locale/country'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './CountrySelector.module.scss'

interface CountrySelectorProps {
  value: PaxCountry | ''
  onChange: (value: PaxCountry) => void
  isRequired?: boolean
  isInvalid?: boolean
  errorMessage?: string
  name: string
  onBlur?: () => void
  inputRef?: (instance: HTMLInputElement | null) => void
  placeholder?: string
  inputClassName?: string
  label?: string
}

const countriesList = Object.entries(countriesData)
  .map(([code, country]) => {
    if (isCountryCode(code)) {
      return {
        code,
        name: country.name,
      } as const
    }
    return null
  })
  .filter(Boolean)
  .sort((a, b) => a.name.localeCompare(b.name))

export default memo(function CountrySelector({
  value,
  onChange,
  isRequired = false,
  isInvalid = false,
  errorMessage,
  name,
  onBlur,
  inputRef,
  placeholder,
  inputClassName,
  label,
}: CountrySelectorProps) {
  const [inputValue, setInputValue] = useState('')
  const [hasInteracted, setHasInteracted] = useState(false)

  const checkAndSetExactMatch = (val: string) => {
    const exactMatch = countriesList.find(c => c.name.toLowerCase() === val.toLowerCase())
    if (exactMatch && isCountryCode(exactMatch.code)) {
      setInputValue(exactMatch.name)
      onChange(exactMatch.code)
      setHasInteracted(false)
      return true
    }
    return false
  }

  const filteredCountries = useMemo(() => {
    if (!inputValue) return countriesList
    const searchTerm = inputValue.toLowerCase()
    return countriesList.filter(c => c.name.toLowerCase().includes(searchTerm))
  }, [inputValue])

  useEffect(() => {
    const country = countriesList.find(c => c.code === value)
    setInputValue(country ? country.name : '')
  }, [value])

  const handleSelectionChange = (key: Key | null) => {
    const country = countriesList.find(c => c.code === key)
    if (country && isCountryCode(country.code)) {
      setInputValue(country.name)
      onChange(country.code)
      setHasInteracted(false)
    } else {
      setInputValue('')
      onChange('' as PaxCountry)
      setHasInteracted(true)
    }
  }

  // When the user types in the box, update what they see.
  // If the box is empty, clear the selection.
  // If the box has something in it, and it exactly matches a known country, auto-select it.
  const handleInputChange = (val: string) => {
    setInputValue(val)
    setHasInteracted(true)
    if (!val.trim()) {
      onChange('' as PaxCountry)
    } else {
      checkAndSetExactMatch(val)
    }
  }

  const handleBlur = () => {
    if (value === '' && inputValue) {
      const matched = checkAndSetExactMatch(inputValue)
      if (!matched) {
        setHasInteracted(true)
        setInputValue('')
      }
    } else {
      setHasInteracted(true)
    }
    onBlur?.()
  }

  const shouldShowError =
    isInvalid ||
    (isRequired &&
      hasInteracted &&
      value === '' &&
      !countriesList.some((c: {name: string}) => c.name.toLowerCase() === inputValue.toLowerCase()))

  return (
    <ComboBox
      className={style.ComboBox}
      selectedKey={value}
      onSelectionChange={handleSelectionChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      isRequired={isRequired}
      isInvalid={shouldShowError}
      menuTrigger='focus'
      shouldFocusWrap
      items={filteredCountries}
    >
      <Label className={style.Label} id='country-label'>
        {label ?? 'Country (as listed on your ID)'}
      </Label>

      <VisuallyHidden id='country-instructions'>
        Start typing or press down arrow to open the list of countries.
      </VisuallyHidden>

      <div className={style.InputWrapper}>
        <Input
          className={inputClassName ?? style.Input}
          autoComplete='off'
          aria-labelledby='country-label'
          aria-describedby='country-instructions'
          name={name}
          ref={inputRef}
          placeholder={placeholder ?? ''}
        />

        <Popover className={style.Popover} placement='bottom start' isNonModal shouldFlip>
          <ListBox className={style.ListBox}>
            {filteredCountries.map(country => (
              <ListBoxItem
                key={country.code}
                id={country.code}
                className={style.ListBoxItem}
                textValue={country.name}
              >
                {country.name}
              </ListBoxItem>
            ))}
          </ListBox>
        </Popover>
      </div>

      {shouldShowError && (
        <FieldError className={style.Error}>{errorMessage ?? 'Country is required'}</FieldError>
      )}
    </ComboBox>
  )
})
